import Box from '@/Atoms/Box';
import React, {useState, useEffect} from 'react';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import CCButton from '@/Atoms/CCButton';
import {FontSizes, vs} from '@/Utils/Dimensions';
import Spacer from '@/Atoms/Spacer';
import CCTextInput from '@/Atoms/CCTextInput';
import CCLoader from '@/Atoms/CCLoader';
import useAlertModalStore from '@/Store/AlertModalStore';
import {styles} from './styles';
import useManageStaffApi from '@/Hooks/SubscriptionApi/useManageStaffApi';
import CCSelect from '@/Atoms/CCSelect';

function StaffManagement() {
  // State variables
  const [mobileNumber, setMobileNumber] = useState('');
  const [apiPassword, setApiPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [userStaffStatus, setUserStaffStatus] = useState(null);
  const [actionType, setActionType] = useState('check'); // 'check', 'grant', 'remove'

  const openAlertModal = useAlertModalStore(state => state.openModal);

  // Action type options
  const actionOptions = [
    {label: 'Check Status', value: 'check'},
    {label: 'Grant Staff Access', value: 'grant'},
    {label: 'Remove Staff Access', value: 'remove'},
  ];

  // Reset states when mobile number changes
  useEffect(() => {
    setUserStaffStatus(null);
    setSuccess(false);
    setErrorMsg(null);
  }, [mobileNumber]);

  // Handle API responses
  const handleStaffUpdateSuccess = responseData => {
    console.log('Staff Update Success:', responseData);
    setSuccess(true);
    setSuccessMessage(
      responseData.message || 'Staff status updated successfully!',
    );
    setUserStaffStatus(responseData.is_staff);
  };

  // Handle API errors
  const handleStaffUpdateError = error => {
    console.error('Staff Update Error:', error);
    if (error.response && error.response.data) {
      setErrorMsg(
        error.response.data.message ||
          error.response.data.error ||
          'Failed to update staff status. Please try again.',
      );
    } else {
      setErrorMsg(
        error.message || 'Failed to update staff status. Please try again.',
      );
    }
    setSuccess(false);
  };

  // API mutation hook
  const {
    mutate: updateStaffStatus,
    isLoading: updatingStaffStatus,
    error: staffUpdateError,
  } = useManageStaffApi(handleStaffUpdateSuccess, handleStaffUpdateError);

  const handleAction = () => {
    setErrorMsg(null);
    setSuccess(false);

    // Validate API password
    if (!apiPassword) {
      setErrorMsg('API Password is required');
      return;
    }

    // Validate mobile number
    if (!mobileNumber.trim()) {
      setErrorMsg('Please enter a mobile number');
      return;
    }

    // For "check" action, just query the user status
    if (actionType === 'check') {
      const payload = {
        mobile_number: mobileNumber,
        action: 'check',
        apiPassword: apiPassword,
      };
      updateStaffStatus(payload);
      return;
    }

    // For grant/remove, confirm first
    const actionVerb = actionType === 'grant' ? 'grant' : 'remove';
    const isStaffValue = actionType === 'grant';

    // Confirm staff status update
    openAlertModal({
      header: `Confirm Staff Access Change`,
      bodyTxt: `You are about to ${actionVerb} STAFF access ${
        actionVerb === 'grant' ? 'to' : 'from'
      } user with mobile number: ${mobileNumber}. This will ${
        actionVerb === 'grant'
          ? 'give them administrative capabilities'
          : 'revoke their administrative access'
      }. Continue?`,
      buttonText: `${
        actionVerb.charAt(0).toUpperCase() + actionVerb.slice(1)
      } Access`,
      handleConfirmation: () => {
        const payload = {
          mobile_number: mobileNumber,
          is_staff: isStaffValue,
          apiPassword: apiPassword,
        };

        updateStaffStatus(payload);
      },
    });
  };

  // Determine button text based on action type
  const getButtonText = () => {
    switch (actionType) {
      case 'check':
        return 'Check Status';
      case 'grant':
        return 'Grant Staff Access';
      case 'remove':
        return 'Remove Staff Access';
      default:
        return 'Submit';
    }
  };

  return (
    <Box flex={1}>
      <CCLoader
        loading={updatingStaffStatus}
        textToShow="Processing staff access..."
      />

      {/* Staff Management Form Section */}
      <Box style={styles.section}>
        <CCText style={styles.sectionHeader}>Staff Access Management</CCText>
        <Spacer spacing={vs(12)} />

        <Box style={styles.row}>
          <CCTextInput
            label="Mobile Number"
            value={mobileNumber}
            onChangeText={setMobileNumber}
            style={styles.input}
            placeholder="e.g. 919876543210"
            keyboardType="phone-pad"
          />
        </Box>

        {/* Action Type Selection */}
        <Box style={styles.row}>
          <Box style={styles.input}>
            <CCText
              fontSize={12}
              color={CCColors.TextColor.Grey}
              style={{
                paddingHorizontal: 6,
                position: 'absolute',
                zIndex: 1,
                background: 'white',
                left: '2%',
                top: '-6%',
              }}>
              Action
            </CCText>
            <CCSelect
              selectedValue={actionType}
              onValueChange={setActionType}
              items={actionOptions}
              style={{
                backgroundColor: 'white',
                borderColor: CCColors.BorderBottom,
              }}
            />
          </Box>
        </Box>

        {/* API Password Field */}
        <Box style={styles.row}>
          <CCTextInput
            label="API Password (Required)"
            value={apiPassword}
            onChangeText={setApiPassword}
            style={styles.input}
            placeholder="Enter API password"
            secureTextEntry={true}
          />
        </Box>
      </Box>

      {/* Status Display */}
      {userStaffStatus !== null && (
        <Box style={styles.section}>
          <CCText
            style={{
              fontSize: FontSizes[14],
              fontWeight: 'bold',
              color: userStaffStatus
                ? CCColors.Green
                : CCColors.TextColor.Primary,
              textAlign: 'center',
            }}>
            Current Status: {userStaffStatus ? 'STAFF' : 'NORMAL USER'}
          </CCText>
        </Box>
      )}

      {/* Help Text */}
      <Box style={styles.section}>
        <CCText
          style={{fontSize: FontSizes[12], color: CCColors.TextColor.Grey}}>
          Note: Staff access gives administrative capabilities to the user. You
          can check current status, grant access, or remove access using this
          tool.
        </CCText>
      </Box>

      {/* Error/Success Messages */}
      {errorMsg && <CCText style={styles.errorText}>{errorMsg}</CCText>}
      {success && <CCText style={styles.successText}>{successMessage}</CCText>}

      {/* Action Button */}
      <Box style={styles.buttonContainer}>
        <CCButton onPress={handleAction} type="fill">
          <CCText>{getButtonText()}</CCText>
        </CCButton>
      </Box>
    </Box>
  );
}

export default StaffManagement;
